/**
 * Global RLS error interceptor.
 *
 * Wraps `window.fetch` and watches for PostgREST responses where the user hit
 * a row-level-security wall (HTTP 401/403 with code 42501 / PGRST301 /
 * "permission denied" / "row-level security" message). When detected, it
 * fires a fire-and-forget POST to the `log-admin-error` edge function so
 * admins can see who's hitting permission errors, without each form having
 * to instrument it manually.
 *
 * Safe to call multiple times — guarded by a module-level flag.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

let installed = false;
// In-memory dedupe so a re-render that triggers the same failing query 5x
// doesn't spam the log table.
const recentlyLogged = new Map<string, number>();
const DEDUPE_WINDOW_MS = 30_000;

const isSupabaseRestUrl = (url: string): boolean => {
  if (!SUPABASE_URL) return false;
  // PostgREST table/RPC requests live under /rest/v1/. We deliberately skip
  // /functions/, /auth/, /storage/ — they have their own error semantics.
  return url.startsWith(`${SUPABASE_URL}/rest/v1/`);
};

const extractResource = (url: string): string | null => {
  try {
    const u = new URL(url);
    // /rest/v1/<table>?... or /rest/v1/rpc/<fn>
    const parts = u.pathname.split("/").filter(Boolean);
    const restIdx = parts.indexOf("v1");
    if (restIdx === -1 || restIdx + 1 >= parts.length) return null;
    const next = parts[restIdx + 1];
    if (next === "rpc" && parts[restIdx + 2]) return `rpc:${parts[restIdx + 2]}`;
    return next ?? null;
  } catch {
    return null;
  }
};

const methodToAction = (method: string): string => {
  switch (method.toUpperCase()) {
    case "GET":
    case "HEAD":
      return "select";
    case "POST":
      return "insert";
    case "PATCH":
      return "update";
    case "PUT":
      return "upsert";
    case "DELETE":
      return "delete";
    default:
      return method.toLowerCase();
  }
};

interface PostgrestErrorBody {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
}

const isRlsError = (status: number, body: PostgrestErrorBody | null): boolean => {
  if (status !== 401 && status !== 403) return false;
  const code = body?.code ?? "";
  const msg = (body?.message ?? "").toLowerCase();
  return (
    code === "42501" ||
    code === "PGRST301" ||
    msg.includes("row-level security") ||
    msg.includes("row level security") ||
    msg.includes("violates row-level") ||
    msg.includes("permission denied")
  );
};

const reportRlsViolation = (params: {
  resource: string | null;
  action: string;
  status: number;
  body: PostgrestErrorBody | null;
  authHeader: string | null;
}) => {
  if (!SUPABASE_PROJECT_ID || !ANON_KEY) return;

  const dedupeKey = `${params.resource}|${params.action}|${params.body?.code ?? params.status}`;
  const now = Date.now();
  const last = recentlyLogged.get(dedupeKey) ?? 0;
  if (now - last < DEDUPE_WINDOW_MS) return;
  recentlyLogged.set(dedupeKey, now);

  const url = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/log-admin-error`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: ANON_KEY,
  };
  if (params.authHeader) headers["Authorization"] = params.authHeader;

  const payload = {
    error_type: "rls_violation",
    error_code: params.body?.code ?? String(params.status),
    error_message: params.body?.message ?? `HTTP ${params.status}`,
    resource_type: params.resource,
    attempted_action: params.action,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    metadata: {
      source: "global-interceptor",
      details: params.body?.details ?? null,
      hint: params.body?.hint ?? null,
    },
  };

  // Fire and forget — don't await, don't surface failures.
  fetch(url, { method: "POST", headers, body: JSON.stringify(payload), keepalive: true }).catch(
    () => {
      /* swallow — logging must never break the app */
    },
  );
};

const getAuthHeader = (init: RequestInit | undefined, request: Request | null): string | null => {
  // Headers may live on the Request, on init.headers, or both. Try both.
  const tryHeaders = (h: HeadersInit | undefined | null): string | null => {
    if (!h) return null;
    if (h instanceof Headers) return h.get("Authorization");
    if (Array.isArray(h)) {
      const found = h.find(([k]) => k.toLowerCase() === "authorization");
      return found ? found[1] : null;
    }
    const obj = h as Record<string, string>;
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === "authorization") return obj[k];
    }
    return null;
  };
  return tryHeaders(init?.headers) ?? (request ? request.headers.get("Authorization") : null);
};

export const installRlsInterceptor = (): void => {
  if (installed || typeof window === "undefined" || !SUPABASE_URL) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input as RequestInfo, init);

    try {
      // Cheap exit: only inspect failed PostgREST calls.
      if (response.ok || (response.status !== 401 && response.status !== 403)) {
        return response;
      }

      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input instanceof Request
              ? input.url
              : String(input);

      if (!isSupabaseRestUrl(url)) return response;

      // Clone so we don't consume the body the caller is about to read.
      const cloned = response.clone();
      let body: PostgrestErrorBody | null = null;
      try {
        body = (await cloned.json()) as PostgrestErrorBody;
      } catch {
        body = null;
      }

      if (!isRlsError(response.status, body)) return response;

      const requestObj = input instanceof Request ? input : null;
      const method = (init?.method ?? requestObj?.method ?? "GET").toUpperCase();

      reportRlsViolation({
        resource: extractResource(url),
        action: methodToAction(method),
        status: response.status,
        body,
        authHeader: getAuthHeader(init, requestObj),
      });
    } catch {
      // Never let the interceptor break the request flow.
    }

    return response;
  };
};
